/* Popover that lists every descendant reply of a message and lets the user jump to one. */

import { useState } from "react";
import { HeartPulse, Mail, Megaphone, MessageCircle, MessageSquare, Phone, StickyNote, Video } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v5/components/ui/popover";
import { cn } from "@/v5/lib/utils";
import type { Message } from "@/v5/components/ConversationThread";

const CHANNEL_ICON: Record<Message["channel"], typeof Mail> = {
  sms: MessageSquare,
  whatsapp: MessageCircle,
  email: Mail,
  phone: Phone,
  video: Video,
  note: StickyNote,
  healthnexus: HeartPulse,
  marketing: Megaphone,
};

const CHANNEL_ICON_COLOR: Record<Message["channel"], string> = {
  sms: "text-blue-600",
  whatsapp: "text-emerald-600",
  email: "text-orange-600",
  phone: "text-green-600",
  video: "text-violet-600",
  note: "text-yellow-600",
  healthnexus: "text-cyan-600",
  marketing: "text-teal-600",
};

function formatRowTimestamp(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  }
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function previewText(message: Message): string {
  const raw = (message.content || message.subject || "").trim();
  return raw.length > 120 ? `${raw.slice(0, 120)}\u2026` : raw || "(no content)";
}

interface ReplyThreadPopoverProps {
  replies: Message[];
  onJump: (target: Message) => void;
  /** When true, render the trigger in a light color suitable for dark/solid bubble backgrounds. */
  onDarkBg?: boolean;
}

export function ReplyThreadPopover({ replies, onJump, onDarkBg }: ReplyThreadPopoverProps) {
  const [open, setOpen] = useState(false);

  if (replies.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-xs font-medium hover:underline",
            onDarkBg ? "text-white/90 hover:text-white" : "text-primary",
          )}
        >
          View replies ({replies.length})
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="border-b border-border px-3 py-2 text-xs font-semibold text-muted-foreground">
          Replies in this thread
        </div>
        <ul className="max-h-72 overflow-y-auto py-1">
          {replies.map((reply) => {
            const Icon = CHANNEL_ICON[reply.channel] ?? MessageSquare;
            const iconColor = CHANNEL_ICON_COLOR[reply.channel] ?? "text-slate-500";
            return (
              <li key={reply.id}>
                <button
                  type="button"
                  onClick={() => {
                    onJump(reply);
                    setOpen(false);
                  }}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/60 focus:bg-muted/60 focus:outline-none"
                >
                  <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", iconColor)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-medium text-foreground">
                        {reply.senderName || (reply.sender === "patient" ? "Patient" : "Staff")}
                      </span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {formatRowTimestamp(reply.timestamp)}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {previewText(reply)}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
